import React, { useState } from 'react';
import { Menu, MenuItem, IconButton, Box } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { AiFillWarning } from 'react-icons/ai';
import ProfilePic from '../../components/Common/ProfilePic';
import { setSelectedCompanyId, saveUserCompanyPreference } from '../../utils/companyDataUtils';

interface CompanyDropdownProps {
	userData: any;
	showCompanyName?: boolean;
	tempId?: any;
}

const CompanyDropdown: React.FC<CompanyDropdownProps> = ({ userData, showCompanyName = false, tempId = Date.now() }) =>
{
	const [ anchorEl, setAnchorEl ] = useState<null | HTMLElement>(null);
	const open = Boolean(anchorEl);

	// Function to handle dropdown menu open
	const handleDropdownClick = (event: React.MouseEvent<HTMLElement>) =>
	{
		event.stopPropagation();
		setAnchorEl(event.currentTarget);
	};

	// Function to handle dropdown menu close
	const handleDropdownClose = () =>
	{
		setAnchorEl(null);
	};

	// Helper to get unique identifier for a company/individual account
	const getAccountId = (account: any) => account?.companyId || account?.userId;

	// Helper to check if account is individual
	const isIndividualAccount = (account: any) => account?.role === 'individual';

	// Function to handle company selection
	const handleCompanySelect = (company: any) =>
	{
		// Prevent selection if company is deactivated
		if (company.isActive === 'deactive')
		{
			return;
		}

		const accountId = getAccountId(company);

		// Use companyId for companies, or a special marker for individual
		setSelectedCompanyId(isIndividualAccount(company) ? 'individual' : company.companyId);

		// Save user-specific company preference for persistence across logout/login
		if (userData?.masterId)
		{
			saveUserCompanyPreference(userData.masterId, isIndividualAccount(company) ? 'individual' : company.companyId);
		}

		if (company.isActive === 'pending')
		{
			window.location.href = '/emp';

			return;
		}

		// Check if switching to a different account
		const currentAccountId = getAccountId(userData);

		if (accountId && accountId !== currentAccountId)
		{
			window.location.href = '/news';
		}

		handleDropdownClose();
	};

	// Filter out deactivated companies
	const activeCompanies = userData?.companyList?.filter((company: any) => company?.isActive === 'active') || [];

	// Don't render if there's only one or no active companies
	if (!activeCompanies || !Array.isArray(activeCompanies) || activeCompanies.length <= 1)
	{
		return null;
	}

	return (
		<>
			<div style={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }} onClick={handleDropdownClick}>
				{/* Show current company name if prop is true */}
				{showCompanyName && userData?.companyName && (
					<span style={{ fontSize: '14px', fontWeight: 500 }}>
						{userData.companyName}
					</span>
				)}

				{/* Dropdown arrow for company selection */}
				<IconButton
					size='small'
					sx={{ padding: '4px', marginLeft: showCompanyName ? '0px' : '4px' }}
				>
					<KeyboardArrowDownIcon fontSize='small' />
				</IconButton>
			</div>

			{/* Dropdown menu for company selection */}
			<Menu
				anchorEl={anchorEl}
				open={open}
				onClose={handleDropdownClose}
				anchorOrigin={{
					vertical   : 'bottom',
					horizontal : 'left'
				}}
				transformOrigin={{
					vertical   : 'top',
					horizontal : 'left'
				}}
			>
				{activeCompanies.map((company: any, index: number) =>
				{
					const isIndividual = isIndividualAccount(company);
					const accountId = getAccountId(company);
					const currentAccountId = getAccountId(userData);
					const isSelected = accountId === currentAccountId;

					return (
						<MenuItem
							key={accountId || index}
							onClick={() => handleCompanySelect(company)}
							selected={isSelected}
							disabled={company?.isActive === 'deactive'}
							sx={{
								display       : 'flex',
								alignItems    : 'center',
								gap           : 1.5,
								minWidth      : 200,
								cursor        : company?.isActive === 'deactive' ? 'not-allowed !important' : 'pointer',
								pointerEvents : company?.isActive === 'deactive' ? 'none' : 'auto'
							}}
						>
							<div style={{
								width        : '24px',
								height       : '24px',
								borderRadius : isIndividual ? '50%' : '4px',
								overflow     : 'hidden',
								opacity      : company?.isActive === 'deactive' ? 0.5 : 1
							}}
							>
								<ProfilePic
									imagePath={isIndividual
										? `${company?.userId}_profile_thumbnail.jpeg?t=${tempId}`
										: `${company?.companyId}_logo_thumbnail.jpeg?t=${tempId}`}
									defaultImg={isIndividual ? '/images/logos/user-default.svg' : '/images/logos/In_logo.svg'}
									alt={isIndividual ? 'Individual' : (company?.companyName || 'Company Logo')}
									from={isIndividual ? 'profile' : 'companyProfile'}
									style={{ objectFit: 'cover', height: '24px', width: '24px' }}
									className=''
								/>
							</div>
							<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
								<span>
									{isIndividual ? 'Individual' : (company?.companyName || 'Unknown Company')}
								</span>
								{company?.isActive === 'deactive' && (
									<AiFillWarning
										style={{
											fontSize : '16px',
											color    : 'orange'
										}}
									/>
								)}
							</Box>
						</MenuItem>
					);
				})}
			</Menu>
		</>
	);
};

export default CompanyDropdown;
